import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GetPresignedUrlAction } from "../Redux/Actions/S3UploadAction";

interface FileUploadParams {
  presignedUrlEndpoint: string;
  file: File;
  contentType: string;
}

interface UseS3UploadReturn {
  uploadFile: (
    presignedUrlEndpoint: string,
    file: File,
    contentType?: string
  ) => void;
  isLoading: boolean;
  s3Path: string;
  success: boolean;
  error: string | null;
  resetState: () => void;
}


const uploadToS3WithFormData = async (
  presignedUrl: string,
  fields: Record<string, any>,
  file: File
): Promise<boolean> => {
  try {
    if (typeof presignedUrl !== "string" || !presignedUrl) {
      return false;
    }
    if (!fields || typeof fields !== "object") {
      return false;
    }
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append("file", file);
    const response = await fetch(presignedUrl, {
      method: "POST",
      body: formData,
    });
    if (response.ok || response.status === 204 || response.status === 303) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const useS3Upload = (): UseS3UploadReturn => {
  const dispatch = useDispatch<any>();

  const getPresignedUrlState = useSelector(
    (state: any) => state?.getPresignedUrl
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [presignedURL, setPresignedURL] = useState<string>("");
  const [presignedFields, setPresignedFields] = useState<Record<string, any>>({});
  const [s3Path, setS3Path] = useState<string>("");
  const [fileToUpload, setFileToUpload] = useState<FileUploadParams | null>(null);
  const [uploadStarted, setUploadStarted] = useState<boolean>(false);

  const handleS3Upload = useCallback(async () => {
    if (
      !fileToUpload?.file ||
      !presignedURL ||
      Object.keys(presignedFields).length === 0
    ) {
      setError("Missing file, presigned URL, or fields");
      setIsLoading(false);
      setUploadStarted(false);
      return;
    }
    
    try {
      const uploadSuccessful = await uploadToS3WithFormData(
        presignedURL,
        presignedFields,
        fileToUpload.file
      );

      if (!uploadSuccessful) {
        setError("Failed to upload file to S3");
        setIsLoading(false);
        setUploadStarted(false);
        return;
      }
      
      setSuccess(true);
      setIsLoading(false);
      setUploadStarted(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setIsLoading(false);
      setUploadStarted(false);
    }
  }, [fileToUpload, presignedURL, presignedFields]);

  useEffect(() => {
    if (!uploadStarted) return;

    if (!getPresignedUrlState?.loading && getPresignedUrlState?.data) {
      const responseData = getPresignedUrlState?.data;
      const urlData = responseData?.data?.url;
      const path = responseData?.data?.path || "";

      if (!urlData || typeof urlData !== "object") {
        const errorMsg = "Invalid response structure - missing url data";
        setError(errorMsg);
        setIsLoading(false);
        setUploadStarted(false);
        return;
      }

      const s3Url = urlData.url || "";
      const fields = urlData.fields || {};

      if (!s3Url || typeof s3Url !== "string") {
        const errorMsg = `Invalid S3 URL received. Type: ${typeof s3Url}, Value: ${s3Url}`;
        setError(errorMsg);
        setIsLoading(false);
        setUploadStarted(false);
        return;
      }

      if (!s3Url.startsWith("http://") && !s3Url.startsWith("https://")) {
        const errorMsg = `S3 URL is not a valid HTTP URL: ${s3Url}`;
        setError(errorMsg);
        setIsLoading(false);
        setUploadStarted(false);
        return;
      }

      setPresignedURL(s3Url);
      setPresignedFields(fields);
      setS3Path(path);
    } else if (
      !getPresignedUrlState?.loading &&
      getPresignedUrlState?.status === false &&
      uploadStarted
    ) {
      setError("Failed to get presigned URL from server");
      setIsLoading(false);
      setUploadStarted(false);
    }
  }, [getPresignedUrlState, uploadStarted]);

  useEffect(() => {
    if (
      presignedURL &&
      Object.keys(presignedFields).length > 0 &&
      fileToUpload?.file
    ) {
      handleS3Upload();
    }
  }, [presignedURL, presignedFields, fileToUpload, handleS3Upload]);

  const uploadFile = (
    presignedUrlEndpoint: string,
    file: File,
    contentType?: string
  ) => {
    setIsLoading(true);
    setSuccess(false);
    setError(null);
    setS3Path("");
    setPresignedURL("");
    setPresignedFields({});
    setUploadStarted(true);

    setFileToUpload({
      presignedUrlEndpoint,
      file,
      contentType: contentType || file.type,
    });
    
    const payload = {
      object_name: file.name,
      content_type: contentType || file.type,
    };
    
    dispatch(GetPresignedUrlAction(presignedUrlEndpoint, payload));
  };

  const resetState = useCallback(() => {
    setIsLoading(false);
    setSuccess(false);
    setError(null);
    setPresignedURL("");
    setPresignedFields({});
    setS3Path("");
    setFileToUpload(null);
    setUploadStarted(false);
  }, []);

  return {
    uploadFile,
    isLoading,
    s3Path,
    success,
    error,
    resetState,
  };
};

export default useS3Upload;